/** Domain used in LMS/content preview before assessment personalization. */
export const PREVIEW_TEMPLATE_DOMAIN = 'your-company.com';

export function normalizeTranslationKey(key) {
    if (key == null) return '';
    if (Array.isArray(key)) {
        return normalizeTranslationKey(key[0]);
    }
    return String(key);
}

/** i18n.language may be da-DK while overrides use da. */
export function translationLanguageCodes(lng) {
    if (!lng) return ['en'];
    const codes = [lng];
    const base = lng.split('-')[0];
    if (base && base !== lng) codes.push(base);
    return [...new Set(codes)];
}

export function getAssessmentCompanyDomain(assessmentStore, questionStore) {
    const fromQuestion =
        questionStore?.question?.ui?.presentation?.personalizedData
            ?.companyDomain;
    if (fromQuestion) return fromQuestion;

    let definition = assessmentStore?.assessment?.definition;
    if (typeof definition === 'string') {
        try {
            definition = JSON.parse(definition);
        } catch {
            return null;
        }
    }
    return definition?.personalizeData?.companyDomain || null;
}

function domainsForKey(normalizedKey, companyDomain) {
    const domains = new Set();
    if (companyDomain) domains.add(companyDomain);
    const match = normalizedKey.match(/@([^@\s,<]+)/);
    if (match) domains.add(match[1]);
    return [...domains];
}

/** Keys to check for overrides (personalized value, then preview template). */
export function translationLookupKeys(key, companyDomain) {
    const normalizedKey = normalizeTranslationKey(key);
    if (!normalizedKey) return [];

    const keys = [normalizedKey];

    for (const domain of domainsForKey(normalizedKey, companyDomain)) {
        if (!domain || domain === PREVIEW_TEMPLATE_DOMAIN) continue;
        if (!normalizedKey.includes(domain)) continue;
        const templateKey = normalizedKey.split(domain).join(PREVIEW_TEMPLATE_DOMAIN);
        if (templateKey !== normalizedKey && !keys.includes(templateKey)) {
            keys.push(templateKey);
        }
    }

    return keys;
}

export function lookupTranslationOverride(
    lookupKeys,
    lng,
    { questionTranslations, assessmentTranslations, branding }
) {
    const sources = [
        questionTranslations,
        assessmentTranslations,
        branding,
    ].filter(Boolean);

    const languages = translationLanguageCodes(lng);

    for (const language of languages) {
        for (const key of lookupKeys) {
            const fullKey = `ui.translation.${language}.${key}`;
            for (const source of sources) {
                const override = source[fullKey];
                if (
                    override !== undefined &&
                    override !== null &&
                    override !== ''
                ) {
                    return override;
                }
            }
        }
    }

    // Last resort: match ui.translation.<any-lang>.<key> (language code drift)
    if (branding) {
        for (const key of lookupKeys) {
            const suffix = `.${key}`;
            for (const [settingName, override] of Object.entries(branding)) {
                if (
                    settingName.startsWith('ui.translation.') &&
                    settingName.endsWith(suffix) &&
                    override !== undefined &&
                    override !== null &&
                    override !== ''
                ) {
                    return override;
                }
            }
        }
    }

    return null;
}
